// game.js - CLOP (Bennett Foddy, 2012) ported from the decompiled foddy.net
// build of CLOP.swf. Game logic is a line-for-line port of Horse.as,
// PlayState.as, Effector.as, Puff.as, Star.as, KeyGraphic.as, SpeechBubble.as,
// LowFlxSprite.as and PhysicsData.as. Physics is the real Nape engine
// (nape-haxe4, compiled from Haxe to JS).
'use strict';

const NAPE = window.nape;
const { Vec2 } = NAPE.geom;
const { Body, BodyType, Material, FluidProperties } = NAPE.phys;
const { Polygon, Circle } = NAPE.shape;
const { Space, Broadphase } = NAPE.space;
const { InteractionFilter } = NAPE.dynamics;
const { PivotJoint, WeldJoint, AngleJoint, MotorJoint } = NAPE.constraint;
const { CbType, CbEvent, InteractionType, InteractionListener, OptionType } = NAPE.callbacks;

const TWO_PI = Math.PI * 2;
function i32(v) { return v | 0; }

// ---------------------------------------------------------------------------
// PhysicsData - the static ground body ("piecesofground"), exported from
// PhysicsEditor into PhysicsData.as. 479 convex polygons, one static body.
// ---------------------------------------------------------------------------
const PhysicsData = {
  createBody(name) {
    if (name !== 'piecesofground') throw new Error('unknown body ' + name);
    const body = new Body();
    body.type = BodyType.STATIC;
    const mat = new Material(0, 1, 2, 0.01, 0.1);
    const filter = new InteractionFilter(1, -1, 1, -1, 1, 0);
    const fprops = new FluidProperties(0, 1);
    body.userData.name = 'ground';
    for (const poly of CLOP_DATA.groundPolys) {
      const verts = poly.map((p) => new Vec2(p[0], p[1]));
      const shape = new Polygon(verts, mat, filter);
      shape.body = body;
      shape.fluidEnabled = false;
      shape.fluidProperties = fprops;
    }
    const anchor = body.localCOM.copy();
    body.translateShapes(new Vec2(-anchor.x, -anchor.y));
    body.position.setxy(0, 0);
    return body;
  },
};

// ---------------------------------------------------------------------------
// Effector - drives the phase of one leg's gait cycle
// ---------------------------------------------------------------------------
class Effector extends FlxSprite {
  constructor(x, y, parent, targetPhase = 0, color = -256, speed = 7.75) {
    super(x, y, null);
    this.phase = 0;
    this.targetPhase = targetPhase;
    this.parent = parent;
    this.speed = speed;
    this.apex = new FlxPoint();
    this.makeGraphic(2, 2, color >>> 0);
    this.exists = true;
  }
  update() {
    const target = this.targetPhase < this.phase ? this.targetPhase + 1 : this.targetPhase;
    this.phase = this.phase * (1 - this.speed * 0.01) + target * (this.speed * 0.01);
    while (this.phase > 1) --this.phase;
    super.update();
  }
}

// ---------------------------------------------------------------------------
// Puff - hoof dust particle coloured from the framebuffer under the hoof
// ---------------------------------------------------------------------------
class Puff extends FlxParticle {
  constructor(x = 0, y = 0, color = 0xffffffff) {
    super();
    this.makeGraphic(2, 2, color);
    this.visible = false;
  }
  onEmit() {
    this.visible = true;
    this.color = FlxG.camera.getPixel32(this.x - FlxG.camera.scroll.x, this.y - FlxG.camera.scroll.y + 4);
  }
  update() {
    super.update();
    if (!this.onScreen()) this.kill();
  }
}

class Star extends FlxSprite {
  constructor(x, y) {
    super(x, y, null);
    this.loadGraphic('Star_StarImg', true, false, 48, 48, false);
    this.addAnimation('spin', [0, 1, 2, 3, 4, 5, 6, 7], 15, true);
    this.play('spin');
  }
}

class KeyGraphic extends FlxSprite {
  constructor(which = 0) {
    super(0, 0, null);
    this.loadGraphic('KeyGraphic_ImgKeys', true, false, 20, 20, false);
    switch (which) {
      case 0: this.addAnimation('up', [0, 0, 0, 4], 10, true); this.addAnimation('down', [4], 0, false); break;
      case 1: this.addAnimation('up', [1, 1, 5, 1], 10, true); this.addAnimation('down', [5], 0, false); break;
      case 2: this.addAnimation('up', [2, 6, 2, 2], 10, true); this.addAnimation('down', [6], 0, false); break;
      case 3: this.addAnimation('up', [7, 3, 3, 3], 10, true); this.addAnimation('down', [7], 0, false); break;
    }
    this.play('up');
    this.y = FlxG.height - 28;
  }
}

class LowFlxSprite extends FlxSprite {
  constructor(x = 0, y = 0) { super(x, y, null); }
  update() { this.active = this.onScreen(); super.update(); }
  draw() { super.draw(); this.active = this.onScreen(); }
}

class SpeechBubble extends FlxGroup {
  constructor(who = null, text = '...') {
    super();
    this.char = who;
    this.ribbon = new FlxSprite(0, 0, 'SpeechBubble_ImgRibbon');
    this.ribbon.scrollFactor = new FlxPoint(0, 0);
    switch (who) {
      case 'Clop':
        this.portrait = new FlxSprite(FlxG.width, 110, 'SpeechBubble_ImgClop');
        this.speech = new FlxText(FlxG.width, 120, FlxG.width - 80 - 4, text, true);
        this.speech.setFormat('EnglishTowne', 19, 0xFFFFFF80, 'right', 0xFF000000);
        this.ribbon.y = 121; this.ribbon.x = FlxG.width;
        break;
      case 'Timmy':
        this.portrait = new FlxSprite(-80, 14, 'SpeechBubble_ImgTimmy');
        this.speech = new FlxText(-FlxG.width - 80 - 4, 24, FlxG.width - 80 - 4, text, true);
        this.speech.setFormat('EnglishTowne', 19, 0xFFFFFF80, 'left', 0xFF000000);
        this.ribbon.y = 24; this.ribbon.x = -295;
        break;
    }
    this.portrait.scrollFactor = new FlxPoint(0, 0);
    this.speech.scrollFactor = new FlxPoint(0, 0);
    this.add(this.ribbon); this.add(this.portrait); this.add(this.speech);
  }
  show(delay = 0) {
    if (this.char === 'Clop') {
      TweenNano.to(this.portrait, 0.3, { alpha: 1, x: FlxG.width - 80, delay: delay });
      TweenNano.to(this.speech, 0.5, { alpha: 1, x: 4, delay: delay });
      TweenNano.to(this.ribbon, 0.4, { alpha: 1, x: -30, delay: delay });
    } else if (this.char === 'Timmy') {
      TweenNano.to(this.portrait, 0.3, { alpha: 1, x: 0, delay: delay });
      TweenNano.to(this.speech, 0.5, { alpha: 1, x: 84, delay: delay });
      TweenNano.to(this.ribbon, 0.4, { alpha: 1, x: 59, delay: delay });
    }
  }
  hide(delay = 0) {
    if (this.char === 'Clop') {
      TweenNano.to(this.portrait, 0.3, { alpha: 0, x: FlxG.width, delay: delay });
      TweenNano.to(this.speech, 0.5, { alpha: 0, x: FlxG.width, delay: delay });
      TweenNano.to(this.ribbon, 0.4, { alpha: 0, x: FlxG.width, delay: delay });
    } else if (this.char === 'Timmy') {
      TweenNano.to(this.portrait, 0.3, { alpha: 0, x: -80, delay: delay });
      TweenNano.to(this.speech, 0.5, { alpha: 0, x: -FlxG.width - 80 - 4, delay: delay });
      TweenNano.to(this.ribbon, 0.4, { alpha: 0, x: -295, delay: delay });
    }
  }
}

// ---------------------------------------------------------------------------
// Horse - the ragdoll unicorn (Horse.as)
// ---------------------------------------------------------------------------
const FRONT_POSITIONS = [
  [0, -1.5700000000000003, -0.019999999999999574, 3.16],
  [-0.3967801511659008, -1.5569662405166378, -0.01988178180886635, 3.1600156093633345],
  [0.024091922299307456, -2.0282611672397053, -0.06403137632534195, 2.777724106072823],
  [-0.14095813780338506, -1.9928060329484865, -0.05015071252908765, 1.4253456415668637],
  [0.20477310573194973, -2.117809675094948, -0.9005407412973745, 1.9190950278563936],
  [0.23982209627132978, -1.846558435726097, -1.544259248284626, 1.591266042008031],
  [-0.008982432893391845, -0.8518091480331336, -2.0274417470598363, 2.350204232437242],
  [0.24304281921661364, -0.3774480809042249, -1.9743779994355686, 1.2005261055571168],
  [0.15098608616983178, 0.09573059144477991, -1.7173769871518738, 1.6075991884750387],
  [0.20718735469931904, -0.019769585678922397, -1.4816487038893102, 1.9560159860521873],
  [0.5053460250903647, -0.5992759280224229, -1.0870293787153456, 2.499777115836199],
  [0.6993816376737225, -0.8763606108480557, -0.5004076726958111, 2.1862568274039136],
  [1.079557435177577, -1.4537943243588254, -0.18090336628029924, 2.779566906197882],
  [0.9341251797635852, -1.4497488100328093, -0.12852737100147138, 2.932293064058628],
  [0.9759865798545215, -1.8775135494209776, -0.1715706907814436, 2.9171490428557076],
  [0.4075015231961414, -1.383649173487477, -0.25679886587608447, 3.1230644652893806],
  [-0.18902116592177087, -1.1055822194061173, -0.3402548228944937, 3.149917377452816],
];
const BACK_POSITIONS = [
  [-0.4400000000000004, 1.5700000000000003, -2.84, 3.33],
  [-0.5637668086329395, 1.3343200158010413, -2.792876971314133, 3.332874957742094],
  [-0.4386724684315979, 0.9720953868054192, -3.007258116997934, 2.7133022176490416],
  [-0.6768812750012323, 1.421150770592499, -3.183362135625486, 1.8035353298398475],
  [-0.6164344455186139, 0.825346645769188, -2.534338328780708, 1.7795776036445878],
  [-0.3358907409564713, 0.6268875630236632, -2.298043013836722, 2.4985184419352766],
  [-0.2643121465584919, 0.5433695550299742, -1.79145387947265, 1.676709123210693],
  [-0.24318232742525137, 0.3591815564636702, -1.4255139760906506, 1.7704511870842428],
  [-0.2229043683077272, 0.47031905165607046, -1.4326447637883035, 1.8677680916128798],
  [0.06338525750275803, 0.4884250114550204, -1.62059447394733, 2.2166488831092526],
  [0.2600076678276544, 0.46835879501667765, -1.7027941784615876, 2.4134265209755412],
  [0.22316919966583715, 0.6284544339586642, -1.7289618441553296, 2.799054419207315],
  [-0.01652376689217583, 1.8450348772688443, -2.459047677074717, 1.9862321497346906],
  [0.06807099320818377, 1.5836771022077487, -2.417260471933523, 2.762301511988402],
  [-0.3370496570482002, 2.128457274274795, -2.8772096030074694, 2.6244043384352276],
  [-0.002399562598525584, 1.2862375180764323, -2.4303213506568957, 2.8621423096069583],
  [-0.2581671408358108, 0.9849156303785662, -2.255204560220882, 3.0289030277074627],
];
const ROT_ARRAY = Array.from({ length: 64 }, (_, i) => i);

class Horse extends FlxGroup {
  constructor(x, y, space, alpha = 1, motorsOn = true) {
    super();
    this._controlsEnabled = false;
    this._speedFactor = 27;
    this._legPower = 28500;
    this._legDamping = 21;
    this._legStaticFriction = 2.125;
    this._legDynamicFriction = 1.125;
    this._legRollingFriction = 117.875;
    this.ff = 0;
    this._soundTimer = 0;
    this._motorsOn = motorsOn;
    this._standTimers = [0, 0, 0, 0];
    this._alpha = alpha;
    this._legs = [this._FR = [], this._FL = [], this._BR = [], this._BL = []];
    this._legAngleJoints = [this._FRAJ = [], this._FLAJ = [], this._BRAJ = [], this._BLAJ = []];
    this._legPivotJoints = [this._FRPJ = [], this._FLPJ = [], this._BRPJ = [], this._BLPJ = []];
    this._legMotorJoints = [this._FRMJ = [], this._FLMJ = [], this._BRMJ = [], this._BLMJ = []];
    this._referenceAngles = [];
    this._space = space;
    this._rootX = x;
    this._rootY = y;
    this.createBodies();
    this._groundLevel = this._rootY + 108;
    this._effectors = [];
    this._effectors.push(this._e1 = new Effector(this._rfleg4.position.x, this._rfleg4.position.y, this._body2, 0, 0xffff0000, 7.75));
    this._effectors.push(this._e2 = new Effector(this._lfleg4.position.x, this._lfleg4.position.y, this._body2, 0, 0xffffff00, 7.75));
    this._effectors.push(this._e3 = new Effector(this._rbleg4.position.x, this._rbleg4.position.y, this._body1, 0, 0xff00ff00, 7.75));
    this._effectors.push(this._e4 = new Effector(this._lbleg4.position.x, this._lbleg4.position.y, this._body1, 0, 0xff00ffff, 7.75));
    this._k1ready = this._k2ready = this._k3ready = this._k4ready = true;
    this._k1 = this._k2 = this._k3 = this._k4 = false;
    this._speedAverage = 0;
    this._speedArray = [0];
    this._frontPositions = FRONT_POSITIONS;
    this._backPositions = BACK_POSITIONS;
    this._targetAngles = [];
    for (let i = 0; i < 4; i++) this._targetAngles.push([0, 0, 0, 0]);
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) this._targetAngles[i][j] = i < 2 ? this._frontPositions[0][j] : this._backPositions[0][j];
    const snd = (name, vol) => { const s = new FlxSound(); s.loadEmbedded(name, false); s.volume = vol; this.add(s); return s; };
    this._clop1 = snd('Clop1Snd', 0.8);
    this._clop2 = snd('Clop2Snd', 0.8);
    this._clop3 = snd('Clop3Snd', 0.8);
    this._clop4 = snd('Clop4Snd', 0.8);
    this._neigh = snd('Neigh2Snd', 0.8);
    this._snort = snd('SnortSnd', 0.5);
    this._whinny = snd('WhinnySnd', 0.8);
    this._controlsEnabled = true;
  }

  neigh() {
    if (this._soundTimer > 5) { this._neigh.play(); this._soundTimer = 0; }
  }

  update() {
    this.ff += FlxG.elapsed;
    this._speedAverage = 0;
    this._speedArray.push(this._body1.velocity.x);
    while (this._speedArray.length > 55) this._speedArray.shift();
    for (let i = 0; i < this._speedArray.length; i++) this._speedAverage += this._speedArray[i];
    this._speedAverage /= 48 * this._speedArray.length;
    if (this._body1.velocity.x - this._speedAverage > 4 && this._speedAverage < 0) {
      if (this._soundTimer > 5) { this._whinny.play(); this._soundTimer = 0; }
    } else if (this._speedAverage < -3) {
      if (this._soundTimer > 5) { this._snort.play(); this._soundTimer = 0; }
    }
    this._soundTimer += FlxG.elapsed;
    for (const name in this.bodyArray) {
      const body = this.bodyArray[name];
      const spr = this.spriteArray[name];
      spr.x = body.position.x - spr.width / 2;
      spr.y = body.position.y - spr.height / 2;
      let rot = body.rotation;
      while (rot < 0) rot += TWO_PI;
      while (rot > TWO_PI) rot -= TWO_PI;
      spr.frame = Math.round(64 * (rot / TWO_PI)) % 64;
      if (name === 'head') {
        const p = body.localPointToWorld(new Vec2(7.56, -12.013));
        const horn = this.spriteArray.horn;
        horn.x = p.x - 12;
        horn.y = p.y - 12;
        rot += Math.PI / 2;
        while (rot < 0) rot += TWO_PI;
        while (rot > TWO_PI) rot -= TWO_PI;
        horn.frame = Math.round(64 * (rot / TWO_PI)) % 64;
      }
    }
    if (this._controlsEnabled) {
      const keys = FlxG.keys;
      const handle = (key, idx, clop) => {
        if (keys.justPressed(key)) { this._standTimers[idx] = 0; this._effectors[idx].targetPhase = 0.5; clop.play(); }
        if (keys.justReleased(key)) this._effectors[idx].targetPhase = 1;
        if (!keys.pressed(key)) this._standTimers[idx] += FlxG.elapsed;
      };
      handle('K', 0, this._clop1);
      handle('H', 1, this._clop2);
      handle('J', 2, this._clop3);
      handle('L', 3, this._clop4);
    }
    for (const e of this._effectors) e.update();
    this.calculateAngles();
    this.reach();
    super.update();
  }

  calculateAngles() {
    for (let i = 0; i < 4; i++) {
      let phase = Number(this._effectors[i].phase);
      if (phase > 1) phase = 1;
      if (phase < 0) phase = 0;
      const lo = Math.floor(phase * 16), hi = Math.ceil(phase * 16);
      const frac = (phase * 16) % 1;
      const table = i < 2 ? this._frontPositions : this._backPositions;
      for (let j = 0; j < 4; j++) {
        if (this._standTimers[i] > 1) this._targetAngles[i][j] = table[0][j];
        else this._targetAngles[i][j] = table[lo][j] * (1 - frac) + table[hi][j] * frac;
      }
    }
  }

  reach() {
    const sf = this._speedFactor;
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        let cur;
        if (j === 0) cur = this._legs[i][0].rotation - (i < 2 ? this._body2.rotation : this._body1.rotation);
        else cur = this._legs[i][j].rotation - this._legs[i][j - 1].rotation;
        const diff = Number(this._targetAngles[i][j]) - cur;
        this._legMotorJoints[i][j].rate = i < 2 ? diff * sf * 0.8 : diff * sf;
      }
    }
    this.j_b1m.rate = (0.05 - (this._body2.rotation - this._body1.rotation)) * sf;
    this.j_b2m.rate = (0.87 - (this._body3.rotation - this._body2.rotation)) * sf;
    this.j_b3m.rate = (-0.07 - (this._body4.rotation - this._body3.rotation)) * sf;
    this.j_b4m.rate = (-1.79 - (this._head.rotation - this._body4.rotation)) * sf;
  }

  createBodies() {
    this.bodyArray = {};
    this.spriteArray = {};
    const legMat = new Material(0, this._legStaticFriction, this._legDynamicFriction, 0.5, this._legRollingFriction);
    const legFilter = new InteractionFilter(2, i32(4294967225));
    const tailFilter = new InteractionFilter(4, i32(4294967295 - 4 - 8 - 2 - 1), -1, -1, 8, i32(4294967295));
    const hairFilter = new InteractionFilter(8, i32(4294967295 - 8 - 4 - 2), 2, 2);
    const legPower = this._legPower, legPower2 = this._legPower * 2;
    const pivotMaxForce = 2500, pivotFreq = 1000000, pivotDamp = 100000, angleDamp = 10000000;
    const legDamping = this._legDamping;
    const hoofMat = new Material(0, 0.5, 0.5, 1);
    const hoofFilter = new InteractionFilter(128, 0, 0, i32(4294967295), 64, i32(4294967295));
    const hoofFluid = new FluidProperties(0, 1);
    const hoofRadius = 5;
    const rx = this._rootX, ry = this._rootY;

    const sprite = (img, size, withAlpha = true) => {
      const s = new FlxSprite(0, 0, null);
      s.width = size; s.height = size;
      if (withAlpha) s.alpha = this._alpha;
      s.origin = new FlxPoint(size / 2, size / 2);
      s.loadGraphic(img, true, false, size, size, true);
      s.addAnimation('rotate', ROT_ARRAY, 0, false);
      s.play('rotate');
      s.rotationFrames = 64;
      return s;
    };
    const hoof = () => {
      const c = new Circle(hoofRadius, null, hoofMat, hoofFilter);
      c.fluidEnabled = true;
      c.fluidProperties = hoofFluid;
      return c;
    };
    // Creates a body part exactly in the order the original does: sprite,
    // body, shapes, rotation, add to space, register sprite + body.
    const part = (name, img, size, px, py, bw, bh, rot, filter, opts = {}) => {
      const s = sprite(img, size, opts.withAlpha !== false);
      const b = new Body();
      b.disableCCD = true; // the SWF's Nape 2.0 beta had no continuous collision detection
      if (opts.userName) b.userData.name = opts.userName;
      b.position = new Vec2(px + rx, py + ry);
      if (opts.hoof) b.shapes.add(opts.hoof);
      const poly = new Polygon(Polygon.box(bw, bh), legMat, filter);
      b.shapes.add(poly);
      if (rot != null) b.rotation = rot;
      this._space.bodies.add(b);
      this.spriteArray[name] = this.add(s);
      this.bodyArray[name] = b;
      return b;
    };
    this._rfleg2 = part('rfleg2', 'Horse_RFLeg2Img', 36, 37.33, 65.88, 0.53 * 48, 0.25 * 48, TWO_PI - 1.57, legFilter);
    this._rfleg1 = part('rfleg1', 'Horse_RFLeg1Img', 28, 36.16, 53.21, 0.38 * 48, 0.37 * 48, TWO_PI + 0, legFilter);
    this._rfleg3 = part('rfleg3', 'Horse_RFLeg3Img', 24, 37.66, 82.88, 0.34 * 48, 0.09 * 48, TWO_PI - 1.59, legFilter);
    this._hoofEnd1 = hoof();
    this._rfleg4 = part('rfleg4', 'Horse_RFLeg4Img', 16, 35.66, 91.38, 0.19 * 48, 0.19 * 48, TWO_PI + 1.57, legFilter, { hoof: this._hoofEnd1 });
    this._rbleg2 = part('rbleg2', 'Horse_RBLeg2Img', 36, 89.66, 64.88, 0.54 * 48, 0.25 * 48, TWO_PI + 1.08, legFilter);
    this._rbleg1 = part('rbleg1', 'Horse_RBLeg1Img', 36, 82.82, 51.21, 0.49 * 48, 0.5 * 48, TWO_PI - 0.49, legFilter);
    this._rbleg3 = part('rbleg3', 'Horse_RBLeg3Img', 28, 95.91, 80.75, 0.43 * 48, 0.11 * 48, TWO_PI - 1.76, legFilter);
    this._hoofEnd3 = hoof();
    this._rbleg4 = part('rbleg4', 'Horse_RBLeg4Img', 16, 95.83, 91.38, 0.2 * 48, 0.15 * 48, TWO_PI + 1.57, legFilter, { hoof: this._hoofEnd3 });
    this._tail1 = part('tail1', 'Horse_Tail1Img', 16, 89.43, 36.19, 0.23 * 48, 0.08 * 48, TWO_PI + 0.31, tailFilter);
    this._tail2 = part('tail2', 'Horse_Tail2Img', 16, 95.53, 40.19, 0.22 * 48, 0.09 * 48, TWO_PI + 0.91, tailFilter);
    this._tail3 = part('tail3', 'Horse_Tail3Img', 20, 99.1, 47.38, 0.31 * 48, 0.15 * 48, TWO_PI + 1.37, tailFilter);
    this._tail4 = part('tail4', 'Horse_Tail4Img', 32, 99.9, 61.88, 0.56 * 48, 0.17 * 48, TWO_PI + 1.57, tailFilter);
    this._hair1 = part('hair1', 'Horse_HairImg', 16, 38.28, 29.19, 0.22 * 48, 0.23 * 48, null, hairFilter, { userName: 'hair', withAlpha: false });
    this._hair2 = part('hair2', 'Horse_HairImg', 16, 33.22, 24.63, 0.22 * 48, 0.23 * 48, TWO_PI + 0.8, hairFilter, { userName: 'hair', withAlpha: false });
    this._hair3 = part('hair3', 'Horse_HairImg', 16, 28.66, 20.06, 0.22 * 48, 0.23 * 48, TWO_PI + 0.51, hairFilter, { userName: 'hair', withAlpha: false });
    this._hair4 = part('hair4', 'Horse_HairImg', 16, 22.66, 17.06, 0.22 * 48, 0.23 * 48, TWO_PI + 0.36, hairFilter, { userName: 'hair', withAlpha: false });
    this._hair5 = part('hair5', 'Horse_HairImg', 16, 17.03, 15.44, 0.22 * 48, 0.23 * 48, TWO_PI + 0.18, hairFilter, { withAlpha: false });
    this._body1 = part('body1', 'Horse_Body1Img', 48, 74.41, 44.69, 0.78 * 48, 0.53 * 48, TWO_PI - 0.05, legFilter, { userName: 'butt' });
    this._body2 = part('body2', 'Horse_Body2Img', 48, 47.66, 45.38, 0.87 * 48, 0.64 * 48, TWO_PI + 0, legFilter, { userName: 'body' });
    this._body3 = part('body3', 'Horse_Body3Img', 32, 31.74, 34.46, 0.47 * 48, 0.47 * 48, TWO_PI + 0.87, legFilter, { userName: 'body' });
    this._body4 = part('body4', 'Horse_Body4Img', 32, 22.03, 24.19, 0.46 * 48, 0.36 * 48, TWO_PI + 0.8, legFilter, { userName: 'body' });
    // head (+ horn collision box) and horn sprite
    {
      const s = sprite('Horse_HeadImg', 36);
      this.spriteArray.head = this.add(s);
      this._head = new Body();
      this._head.disableCCD = true;
      this._head.userData.name = 'head';
      this._head.position = new Vec2(9.16 + rx, 23.56 + ry);
      this._head.shapes.add(new Polygon(Polygon.box(0.53 * 48, 0.34 * 48), legMat, legFilter));
      const hornPoly = new Polygon(Polygon.box(18.188, 4.625), legMat, legFilter);
      this._head.shapes.add(hornPoly);
      hornPoly.rotate(Math.PI / 2);
      hornPoly.translate(new Vec2(7.76, -12.413));
      const hs = sprite('Horse_HornImg', 24);
      this.spriteArray.horn = this.add(hs);
      this._head.rotation = TWO_PI - 1;
      this._space.bodies.add(this._head);
      this.bodyArray.head = this._head;
    }
    this._lfleg1 = part('lfleg1', 'Horse_LFLeg1Img', 28, 36.16, 53.21, 0.38 * 48, 0.37 * 48, TWO_PI + 0, legFilter);
    this._lfleg2 = part('lfleg2', 'Horse_LFLeg2Img', 36, 37.33, 65.88, 0.53 * 48, 0.25 * 48, TWO_PI - 1.57, legFilter);
    this._lfleg3 = part('lfleg3', 'Horse_LFLeg3Img', 24, 37.66, 82.88, 0.34 * 48, 0.09 * 48, TWO_PI - 1.59, legFilter);
    this._hoofEnd2 = hoof();
    this._lfleg4 = part('lfleg4', 'Horse_LFLeg4Img', 16, 35.66, 91.38, 0.19 * 48, 0.19 * 48, TWO_PI + 1.57, legFilter, { hoof: this._hoofEnd2 });
    this._lbleg1 = part('lbleg1', 'Horse_LBLeg1Img', 36, 82.82, 51.21, 0.49 * 48, 0.5 * 48, TWO_PI - 0.49, legFilter);
    this._lbleg2 = part('lbleg2', 'Horse_LBLeg2Img', 36, 89.66, 64.88, 0.54 * 48, 0.25 * 48, TWO_PI + 1.08, legFilter);
    this._lbleg3 = part('lbleg3', 'Horse_LBLeg3Img', 28, 95.91, 80.75, 0.43 * 48, 0.11 * 48, TWO_PI - 1.76, legFilter);
    this._hoofEnd4 = hoof();
    this._lbleg4 = part('lbleg4', 'Horse_LBLeg4Img', 16, 95.83, 91.38, 0.2 * 48, 0.15 * 48, TWO_PI + 1.57, legFilter, { hoof: this._hoofEnd4 });

    const space = this._space;
    const wp = (x, y) => new Vec2(x + rx, y + ry);
    // Hair welds
    const weld = (a, b, x, y) => {
      const p = wp(x, y);
      const j = new WeldJoint(a, b, a.worldPointToLocal(p), b.worldPointToLocal(p));
      space.constraints.add(j);
      j.frequency = 3;
      j.maxForce = 250;
      return j;
    };
    this.j_h1a = weld(this._body3, this._hair1, 38, 28.78);
    this.j_h2a = weld(this._body3, this._hair2, 33, 24.78);
    this.j_h3a = weld(this._body3, this._hair3, 27, 20.77);
    this.j_h4a = weld(this._body4, this._hair4, 22, 18.77);
    this.j_h5a = weld(this._body4, this._hair5, 16, 14.77);
    // Tail
    const tail = (a, b, lim, x, y, freq) => {
      const ref = a.rotation - b.rotation;
      const jb = new AngleJoint(a, b, -lim - ref, lim - ref);
      const p = wp(x, y);
      const ja = new PivotJoint(a, b, a.worldPointToLocal(p), b.worldPointToLocal(p));
      ja.maxForce = pivotMaxForce;
      if (freq) ja.frequency = pivotFreq;
      space.constraints.add(ja);
      space.constraints.add(jb);
      return [ja, jb];
    };
    [this.j_t1a, this.j_t1b] = tail(this._body1, this._tail1, 0.17, 85.81, 35.12, true);
    [this.j_t2a, this.j_t2b] = tail(this._tail1, this._tail2, 0.35, 93.09, 37.77, true);
    [this.j_t3a, this.j_t3b] = tail(this._tail2, this._tail3, 0.52, 98.05, 43.07, false);
    [this.j_t4a, this.j_t4b] = tail(this._tail3, this._tail4, 0.79, 99.04, 52, false);
    // Limb joints: angle limit + pivot + motor
    const limb = (a, b, lo, hi, x, y, motorForce, motorDamping) => {
      const ref = a.rotation - b.rotation;
      const jb = new AngleJoint(a, b, lo - ref, hi - ref);
      const p = wp(x, y);
      const ja = new PivotJoint(a, b, a.worldPointToLocal(p), b.worldPointToLocal(p));
      ja.frequency = pivotFreq;
      ja.maxForce = pivotMaxForce;
      ja.damping = pivotDamp;
      jb.damping = angleDamp;
      const jm = new MotorJoint(a, b);
      jm.maxForce = motorForce;
      if (motorDamping != null) jm.damping = motorDamping;
      space.constraints.add(ja);
      space.constraints.add(jb);
      if (this._motorsOn) space.constraints.add(jm);
      return [ja, jb, jm];
    };
    [this.j_rb1a, this.j_rb1b, this.j_rb1m] = limb(this._body1, this._rbleg1, -0.61, 0.7, 83.5 - 1.5, 44.06, legPower * 4, legDamping);
    [this.j_rb2a, this.j_rb2b, this.j_rb2m] = limb(this._rbleg1, this._rbleg2, -1.22, 1, 83.83, 59.61, legPower * 1.5, legDamping);
    [this.j_rb3a, this.j_rb3b, this.j_rb3m] = limb(this._rbleg2, this._rbleg3, -0.35, 1.92, 94.72, 74.49, legPower, legDamping);
    [this.j_rb4a, this.j_rb4b, this.j_rb4m] = limb(this._rbleg3, this._rbleg4, -2.18, 0, 96.73, 89.05, legPower2, null); // original sets no damping here
    [this.j_lb1a, this.j_lb1b, this.j_lb1m] = limb(this._body1, this._lbleg1, -0.61, 0.7, 83.5, 44.06, legPower * 4, legDamping);
    [this.j_lb2a, this.j_lb2b, this.j_lb2m] = limb(this._lbleg1, this._lbleg2, -1.22, 1, 83.83, 59.61, legPower * 1.5, legDamping);
    [this.j_lb3a, this.j_lb3b, this.j_lb3m] = limb(this._lbleg2, this._lbleg3, -0.35, 1.92, 94.72, 74.49, legPower, legDamping);
    [this.j_lb4a, this.j_lb4b, this.j_lb4m] = limb(this._lbleg3, this._lbleg4, -2.18, 0, 96.73, 89.05, legPower2, legDamping);
    [this.j_rf1a, this.j_rf1b, this.j_rf1m] = limb(this._body2, this._rfleg1, -0.84, 1.32, 32.22, 48.03, legPower * 2, legDamping);
    [this.j_rf2a, this.j_rf2b, this.j_rf2m] = limb(this._rfleg1, this._rfleg2, -1.44, 1.75, 37.52, 58.27, legPower * 1.5, legDamping);
    [this.j_rf3a, this.j_rf3b, this.j_rf3m] = limb(this._rfleg2, this._rfleg3, -2.27, 0, 37.19, 76.15, legPower, legDamping);
    [this.j_rf4a, this.j_rf4b, this.j_rf4m] = limb(this._rfleg3, this._rfleg4, -2.36, 0, 37.85, 89.05, legPower2, legDamping);
    [this.j_lf1a, this.j_lf1b, this.j_lf1m] = limb(this._body2, this._lfleg1, -0.84, 1.32, 32.22, 48.03, legPower * 2, legDamping);
    [this.j_lf2a, this.j_lf2b, this.j_lf2m] = limb(this._lfleg1, this._lfleg2, -1.44, 1.75, 37.52, 58.27, legPower * 1.5, legDamping);
    [this.j_lf3a, this.j_lf3b, this.j_lf3m] = limb(this._lfleg2, this._lfleg3, -2.27, 0, 37.19, 76.15, legPower, legDamping);
    [this.j_lf4a, this.j_lf4b, this.j_lf4m] = limb(this._lfleg3, this._lfleg4, -2.36, 0, 37.85, 89.05, legPower2, legDamping);
    [this.j_b1a, this.j_b1b, this.j_b1m] = limb(this._body1, this._body2, -0.61, 0.61, 63, 37.77, legPower / 1, legDamping * 5);
    [this.j_b2a, this.j_b2b, this.j_b2m] = limb(this._body2, this._body3, -0.57, 0.4, 30.9 + 8.46, 40.09 - 4.9, legPower / 3, legDamping * 5);
    [this.j_b3a, this.j_b3b, this.j_b3m] = limb(this._body3, this._body4, -0.17, 0.26, 25.61, 29.84, legPower / 3, legDamping * 5);
    [this.j_b4a, this.j_b4b, this.j_b4m] = limb(this._body4, this._head, -1.17, 1.87, 17.01, 17.6, legPower / 6, legDamping);

    this._FRAJ.push(this.j_rf1b, this.j_rf2b, this.j_rf3b, this.j_rf4b);
    this._FLAJ.push(this.j_lf1b, this.j_lf2b, this.j_lf3b, this.j_lf4b);
    this._BRAJ.push(this.j_rb1b, this.j_rb2b, this.j_rb3b, this.j_rb4b);
    this._BLAJ.push(this.j_lb1b, this.j_lb2b, this.j_lb3b, this.j_lb4b);
    this._FRPJ.push(this.j_rf1a, this.j_rf2a, this.j_rf3a, this.j_rf4a);
    this._FLPJ.push(this.j_lf1a, this.j_lf2a, this.j_lf3a, this.j_lf4a);
    this._BRPJ.push(this.j_rb1a, this.j_rb2a, this.j_rb3a, this.j_rb4a);
    this._BLPJ.push(this.j_lb1a, this.j_lb2a, this.j_lb3a, this.j_lb4a);
    this._FRMJ.push(this.j_rf1m, this.j_rf2m, this.j_rf3m, this.j_rf4m);
    this._FLMJ.push(this.j_lf1m, this.j_lf2m, this.j_lf3m, this.j_lf4m);
    this._BRMJ.push(this.j_rb1m, this.j_rb2m, this.j_rb3m, this.j_rb4m);
    this._BLMJ.push(this.j_lb1m, this.j_lb2m, this.j_lb3m, this.j_lb4m);
    this._FR.push(this._rfleg1, this._rfleg2, this._rfleg3, this._rfleg4);
    this._BR.push(this._rbleg1, this._rbleg2, this._rbleg3, this._rbleg4);
    this._FL.push(this._lfleg1, this._lfleg2, this._lfleg3, this._lfleg4);
    this._BL.push(this._lbleg1, this._lbleg2, this._lbleg3, this._lbleg4);

    // Every constraint in the space is made soft.
    const it = space.constraints.iterator();
    while (it.hasNext()) it.next().stiff = false;
    for (const arr of this._legAngleJoints) for (const j of arr) j.stiff = false;

    this._referenceAngles.push([
      this._body2.rotation - this._rfleg1.rotation, this._rfleg1.rotation - this._rfleg2.rotation,
      this._rfleg2.rotation - this._rfleg3.rotation, this._rfleg3.rotation - this._rfleg4.rotation]);
    this._referenceAngles.push([
      this._body2.rotation - this._lfleg1.rotation, this._lfleg1.rotation - this._lfleg2.rotation,
      this._lfleg2.rotation - this._lfleg3.rotation, this._lfleg3.rotation - this._lfleg4.rotation]);
    this._referenceAngles.push([
      this._body1.rotation - this._rbleg1.rotation, this._rbleg1.rotation - this._rbleg2.rotation,
      this._rbleg2.rotation - this._rbleg3.rotation, this._rbleg3.rotation - this._rbleg4.rotation]);
    this._referenceAngles.push([
      this._body1.rotation - this._lbleg1.rotation, this._lbleg1.rotation - this._lbleg2.rotation,
      this._lbleg2.rotation - this._lbleg3.rotation, this._lbleg3.rotation - this._lbleg4.rotation]);
    this._referenceAngles.push([this._body1.rotation - this._body2.rotation, this._body2.rotation - this._body3.rotation]);
    this._hoofEndArray = [this._hoofEnd1, this._hoofEnd2, this._hoofEnd3, this._hoofEnd4];
    this._hoofEnd1.translate(new Vec2(4.5, 1.5));
    this._hoofEnd2.translate(new Vec2(4.5, 1.5));
    this._hoofEnd3.translate(new Vec2(4.5, 1.5));
    this._hoofEnd4.translate(new Vec2(4.5, 1.5));
    for (const b of [this._lfleg4, this._rfleg4, this._lbleg4, this._rbleg4, this._lfleg3, this._rfleg3, this._lbleg3, this._rbleg3]) b.userData.name = 'foot';
  }

  destroy() {
    const space = this._space;
    for (const name in this.bodyArray) {
      const b = this.bodyArray[name];
      if (b.space === space) space.bodies.remove(b);
    }
    for (const k in this) {
      if (k.indexOf('j_') === 0 && this[k] && this[k].space === space) space.constraints.remove(this[k]);
    }
    this._legMotorJoints = this._legAngleJoints = this._legPivotJoints = this._legs = null;
    this._referenceAngles = null;
    this._effectors = null;
    this._targetAngles = null;
    this._hoofEndArray = null;
    for (const name in this.spriteArray) {
      const s = this.spriteArray[name];
      this.remove(s, true);
      s.destroy();
    }
    this.spriteArray = null;
    this.bodyArray = null;
    super.destroy();
  }
}

// ---------------------------------------------------------------------------
// PlayState (PlayState.as)
// ---------------------------------------------------------------------------
const LEVEL_IMAGES = [
  'PlayState_ImgLeaf0', 'PlayState_ImgLeaf1', 'PlayState_ImgLeaf2', 'PlayState_ImgGrass0', 'PlayState_ImgGrassEnd',
  'PlayState_ImgLongGrass1', 'PlayState_ImgMediumGrass1', 'PlayState_ImgMediumGrassSlope', 'PlayState_ImgFlagstone',
  'PlayState_ImgSquarestone', 'PlayState_ImgTrunk0', 'PlayState_ImgTrunk1', 'PlayState_ImgRoundstone', 'PlayState_ImgPentastone',
  'PlayState_ImgSmallRock1', 'PlayState_ImgSmallRock2', 'PlayState_ImgSmallRock3', 'PlayState_ImgSmallRock4', 'PlayState_ImgRushes',
  'PlayState_ImgFence1', 'PlayState_ImgFence2', 'PlayState_ImgTuft', 'PlayState_ImgTuft2', 'PlayState_ImgTuftS1', 'PlayState_ImgTuftS2',
  'PlayState_ImgVineLeaf1', 'PlayState_ImgVineLeaf2', 'PlayState_ImgWheat', 'PlayState_ImgOakLeaf', 'PlayState_ImgOakLeaf2',
  'PlayState_ImgOakTrunkFront', 'PlayState_ImgOakTrunkBack', 'PlayState_ImgStoneWall1', 'PlayState_ImgStoneWall2',
  'PlayState_ImgBirchLeaf1', 'PlayState_ImgBirchStatic', 'PlayState_ImgBirchTrunk1', 'PlayState_ImgBirchTrunk2', 'PlayState_ImgHedge1',
  'PlayState_ImgHedgeLeaf1', 'PlayState_ImgHedgeLeaf2', 'PlayState_ImgGrassLow', 'PlayState_ImgGrassLowSlope', 'PlayState_ImgGrass0Slope',
  'PlayState_ImgPoppies', 'PlayState_ImgNettle', 'PlayState_ImgMenhir', 'PlayState_ImgSherrod',
];

class PlayState extends FlxState {
  constructor(focus = false, deathNum = 0) {
    super();
    this.Debug = false;
    this._gravity = 270;
    this.ratio = 48;
    this._seed = 123;
    this._numTouchingGround = 0;
    this._noConFrames = 0;
    this._thudTimer = 0;
    this._deathNum = deathNum;
    this._gameOver = false;
    this._timeElapsed = 0;
    this._resetting = false;
    this._focus = focus;
    this._loserMode = false;
    this._frontKeysInARow = 0;
    this._score = 0;
    this._itemCount = 0;
    this._highScore = NaN;
    this._pause = false;
    this._endKeyListener = false;
    this._images = LEVEL_IMAGES.slice();
    this.cameraReset = false;
    this._horseAlive = true;
    this.BGLayer = new FlxGroup();
    this.add(this.BGLayer);
    this._currentLayer = 3;
    this._layerArray = [];
    this._layerScrollFactors = [];
    const tint = new FlxSprite(0, 0, null);
    tint.makeGraphic(FlxG.width, FlxG.height, 813127082);
    tint.blend = 'hard-light';
    tint.scrollFactor = new FlxPoint(0, 0);
    for (let i = 0; i < 12; i++) {
      const g = new FlxGroup();
      this.add(g);
      this._layerArray.push(g);
      if (i < 10) g.add(tint);
      this._layerScrollFactors.push(new FlxPoint(0.55 + i * 0.05, 0.82 + i * 0.02));
      if (i === 8) {
        this.SpriteLayer = new FlxGroup();
        this.SpriteLayer.add(tint);
        this.add(this.SpriteLayer);
      }
    }
    this._currentSprite = 0;
    this.FrontLayer = new FlxGroup();
    this.add(this.FrontLayer);
    this.setupWorld();
    this.Copyright = new FlxSprite(4, 4, 'PlayState_ImgCopyright');
    this.Copyright.scrollFactor = new FlxPoint(0, 0);
    this.Copyright.alpha = 0;
    this.FrontLayer.add(this.Copyright);
    this._frontKeyCount = 0;
    this._backKeyCount = 0;
    const sky = this._sky = new FlxSprite(0, 0, null);
    sky.loadGraphic('PlayState_ImgSky');
    sky.scrollFactor = new FlxPoint(0, 0);
    this.BGLayer.add(sky);
    // "air" fluid body
    const airFilter = new InteractionFilter(128, 0, 0, i32(3439329279), 8, 8);
    const air = new Body(BodyType.STATIC);
    const airMat = new Material(0, 0.5, 0.5, 1);
    const airPoly = new Polygon(Polygon.rect(-4000, -1000, 8000, 2000), airMat, airFilter);
    air.shapes.add(airPoly);
    air.userData.name = 'air';
    airPoly.fluidEnabled = true;
    airPoly.fluidProperties = new FluidProperties(0, 2.5);
    this._space.bodies.add(air);
    const ground = PhysicsData.createBody('piecesofground');
    ground.position = new Vec2(-3187, 373);
    this._space.bodies.add(ground);
    this._horse = new Horse(210, 110, this._space);
    this.SpriteLayer.add(this._horse);
    const groundImgs = [
      [7424, 1152, 'PlayState_ImgGround00'], [6656, 1152, 'PlayState_ImgGround01'], [5888, 1152, 'PlayState_ImgGround02'],
      [5120, 1152, 'PlayState_ImgGround03'], [4352, 1152, 'PlayState_ImgGround04'], [3584, 1152, 'PlayState_ImgGround05'],
      [2816, 1024, 'PlayState_ImgGround06'], [2304, 1024, 'PlayState_ImgGround07'], [1408, 1280, 'PlayState_ImgGround08'],
      [640, 1408, 'PlayState_ImgGround09'], [0, 1408, 'PlayState_ImgGround10'],
    ];
    for (const [gx, gy, img] of groundImgs) this.SpriteLayer.add(new FlxSprite(-7780 + gx, -1356 + gy, img));
    const star = new Star(-490, -35);
    this.SpriteLayer.add(star);
    TweenNano.to(star, 1, { x: -510, delay: 20 });
    this._cameraTarget = new FlxObject(0, -40, 0, 0);
    this._cameraTarget.x = this._horse._body1.position.x - 80;
    this._cameraTarget.y = this._horse._body1.position.y - 30;
    this._text0 = new FlxText(FlxG.width / 2 - 125, 5, 250, 'null');
    this._text0.text = '';
    this._text0.color = 4281368831;
    this._text0.scrollFactor = new FlxPoint(0, 0);
    this.FrontLayer.add(this._text0);
    this._speedAverage = 0;
    this._speedArray = [];
    for (let i = 0; i < 35; i++) this._speedArray.push(0);
    this._music = new FlxSound(); this._music.loadEmbedded('MusicSnd', true); this._music.volume = 0; this.add(this._music); this._music.play();
    this._wind = new FlxSound(); this._wind.loadEmbedded('WindSnd', true); this._wind.volume = 0.4; this.add(this._wind); this._wind.play();
    this._thud = new FlxSound(); this._thud.loadEmbedded('ThudSnd', false); this.add(this._thud);
    this._startSnd = new FlxSound(); this._startSnd.loadEmbedded('StartSnd', false); this.add(this._startSnd);
    this._endSnd = new FlxSound(); this._endSnd.loadEmbedded('EndSnd', false); this.add(this._endSnd);
    this._deathSnd = new FlxSound(); this._deathSnd.loadEmbedded('Neigh1Snd', false); this.add(this._deathSnd);
    const puffCount = 100;
    this._puffs = new FlxEmitter(5, 1, puffCount);
    this._puffs.setXSpeed(50, 150);
    this._puffs.gravity = 160;
    this._puffs.setYSpeed(-50, -20);
    this._puffs.setRotation(0, 0);
    this._puffs.lifespan = 0.8;
    this._puffs.particleDrag = new FlxPoint(0.25, 0.25);
    this._puffs.width = 12;
    this._puffs.height = 3;
    for (let i = 0; i < puffCount; i++) this._puffs.add(new Puff(0, 0));
    this._layerArray[8].add(this._puffs);
    this._hKey = new KeyGraphic(0);
    this._jKey = new KeyGraphic(1);
    this._kKey = new KeyGraphic(2);
    this._lKey = new KeyGraphic(3);
    this.FrontLayer.add(this._hKey); this.FrontLayer.add(this._jKey); this.FrontLayer.add(this._kKey); this.FrontLayer.add(this._lKey);
    this._hKey.alpha = 0; this._jKey.alpha = 0; this._kKey.alpha = 0; this._lKey.alpha = 0;
    const girl = new FlxSprite(2, FlxG.height - 24, 'PlayState_ImgModelGirl');
    girl.scrollFactor = new FlxPoint(0, 0);
    this.FrontLayer.add(girl);
    const tree = new FlxSprite(FlxG.width - 20, FlxG.height - 37, 'PlayState_ImgModelTree');
    tree.scrollFactor = new FlxPoint(0, 0);
    this.FrontLayer.add(tree);
    this._modelHorse = new FlxSprite(FlxG.width - 20, FlxG.height - 22, 'PlayState_ImgModelHorse');
    this._modelHorse.scrollFactor = new FlxPoint(0, 0);
    this.FrontLayer.add(this._modelHorse);
    this._save = new FlxSave();
    this._save.bind('CLOP');
    const savedScore = Number(this._save.data.highScore);
    this._hsIndicator = new FlxSprite(FlxG.width - 4 - this._highScore * 0.01 * (320 - 8), FlxG.height - 8, 'PlayState_ImgHS');
    this._hsIndicator.scrollFactor = new FlxPoint(0, 0);
    this._timeText = new FlxText(23, FlxG.height - 23, 200, 'Time: 0.0s', true);
    this._timeText.setFormat('EnglishTowne', 16, 0xFFFFFF80, 'left', 0xFF000000);
    this._timeText.scrollFactor = new FlxPoint(0, 0);
    if (savedScore) {
      this._highScore = savedScore;
      if (this._save.data.hasWon) {
        this._bestTime = new FlxText(FlxG.width - 220, FlxG.height - 23, 200, 'Thy best: ' + Number(this._save.data.bestTime).toFixed(1) + 's', true);
        this._bestTime.setFormat('EnglishTowne', 16, 0xFFFFFF80, 'right', 0xFF000000);
        this._bestTime.scrollFactor = new FlxPoint(0, 0);
        this.FrontLayer.add(this._bestTime);
        this.FrontLayer.add(this._timeText);
      } else {
        this._hsIndicator.x = FlxG.width - 4 - this._highScore * 0.01 * (320 - 8);
        this.FrontLayer.add(this._hsIndicator);
      }
    } else {
      this._hsIndicator = new FlxSprite(500, 0, null);
      this._highScore = 0;
      this._save.data.highScore = 0;
      this._save.flush();
    }
    this._clopBubble = new SpeechBubble('Clop', 'Confound thee Sherrod, this had best not be one of thine japes. Thou knowest I mislike hills.');
    this._timmyBubble = new SpeechBubble('Timmy', 'Ho there, CLOP! I hear tell there’s a virgin on the far side of yon high and pleasant hill!');
    this.FrontLayer.add(this._clopBubble);
    this.FrontLayer.add(this._timmyBubble);
    this._focusText = new FlxText(0, FlxG.height - 45, FlxG.width, '< Clicketh mouse to begin >', true);
    this._focusText.setFormat('EnglishTowne', 19, 0xFFB0B0B0, 'center', 0xFF000000);
    this._focusText.scrollFactor = new FlxPoint(0, 0);
    this._needsActivate = false;
    if (!this._focus) {
      this._needsActivate = true;
      this._clopBubble.show(1);
      this._timmyBubble.show(0.4);
      this._pause = true;
      this._hsIndicator.alpha = 0;
      this._startSnd.play();
      this.FrontLayer.add(this._focusText);
    }
    const any = new OptionType(CbType.ANY_BODY);
    this._space.listeners.add(new InteractionListener(CbEvent.BEGIN, InteractionType.COLLISION, any, any, (cb) => this.collisionBegin(cb)));
    this._space.listeners.add(new InteractionListener(CbEvent.END, InteractionType.COLLISION, any, any, (cb) => this.collisionEnd(cb)));
    this._space.listeners.add(new InteractionListener(CbEvent.ONGOING, InteractionType.COLLISION, any, any, (cb) => this.collisionOngoing(cb)));
  }

  // Render scale changed (classic <-> HD): the sky image was swapped by CLOP.applyMode.
  onScaleChange() {
    const sf = this._sky.scrollFactor;
    this._sky.loadGraphic('PlayState_ImgSky');
    this._sky.scrollFactor = sf;
  }
  // Equivalent of the stage ACTIVATE listener in the original.
  onActivate() {
    if (!this._needsActivate) return;
    this._needsActivate = false;
    this.onFocus();
  }
  onFocus() {
    this._clopBubble.hide();
    this._timmyBubble.hide();
    this._pause = false;
    TweenNano.to(this._focusText, 1, { alpha: 0, y: FlxG.height });
    TweenNano.to(this._hKey, 0.6, { alpha: 1 });
    TweenNano.to(this._jKey, 0.6, { alpha: 1 });
    TweenNano.to(this._kKey, 0.6, { alpha: 1 });
    TweenNano.to(this._lKey, 0.6, { alpha: 1 });
    TweenNano.to(this._hsIndicator, 0.6, { alpha: 1 });
    TweenNano.to(this.Copyright, 2, { alpha: 1 });
  }
  onKeyDown() {
    if (this._endKeyListener) this.reset();
  }

  update() {
    if (!this._pause) this._timeElapsed += FlxG.elapsed;
    this._timeText.text = 'Time: ' + this._timeElapsed.toFixed(1) + 's';
    if (!this.cameraReset) {
      FlxG.camera.follow(this._cameraTarget);
      FlxG.camera.setBounds(-8192 + 440, -1000, 8192 - 60, 1640);
      this.cameraReset = true;
      FlxG.camera.bgColor = 0xff000000;
      this.processLevelData();
    }
    if (this._loserMode) {
      const c = (Math.sin(this._timeElapsed * 3) * Math.sin(this._timeElapsed * 3) * 127 + 128) | 0;
      this._text0.color = (255 << 24 | c << 16) >>> 0;
    }
    const keys = FlxG.keys;
    if (keys.justPressed('H') || keys.justPressed('K')) { ++this._frontKeyCount; ++this._frontKeysInARow; }
    if (keys.justPressed('J') || keys.justPressed('L')) { this._frontKeyCount -= 2; this._frontKeysInARow = 0; }
    this._frontKeyCount = Math.max(-50, this._frontKeyCount);
    if (this._frontKeyCount > 100 && this._frontKeysInARow > 5) {
      if (!this._loserMode) {
        this._loserMode = true;
        this._text0.text = "Lame Horse Mode Unlock'd";
        this._text0.setFormat('EnglishTowne', 19, 0xFFFFFF80, 'center', 0xFF000000);
        this._deathSnd.play();
        TweenNano.to(this._hsIndicator, 3, { alpha: 0 });
        TweenNano.to(this._modelHorse, 3, { alpha: 0 });
        this._text0.color = 0xFFFF0000;
        const h = this._horse;
        h.j_lb3m.active = false; h.j_lb4m.active = false; h.j_rb3m.active = false; h.j_rb4m.active = false;
        h.j_lb2m.active = false; h.j_lb1m.active = false; h.j_rb2m.active = false; h.j_rb1m.active = false;
      }
    }
    this._hKey.x = this._horse.spriteArray.lfleg4.x + 8;
    this._jKey.x = this._horse.spriteArray.rbleg4.x - 8;
    this._kKey.x = this._horse.spriteArray.rfleg4.x - 8;
    this._lKey.x = this._horse.spriteArray.lbleg4.x + 8;
    if (this._hKey.alpha === 1 && keys.justPressed('H')) TweenNano.to(this._hKey, 1, { alpha: 0 });
    if (this._jKey.alpha === 1 && keys.justPressed('J')) TweenNano.to(this._jKey, 1, { alpha: 0 });
    if (this._kKey.alpha === 1 && keys.justPressed('K')) TweenNano.to(this._kKey, 1, { alpha: 0 });
    if (this._lKey.alpha === 1 && keys.justPressed('L')) TweenNano.to(this._lKey, 1, { alpha: 0 });
    if (this.Copyright.alpha === 1) {
      if (this._lKey.alpha === 0 && this._hKey.alpha === 0) TweenNano.to(this.Copyright, 2, { alpha: 0 });
    }
    this._speedAverage = 0;
    this._speedArray.push(this._horse._body1.velocity.x);
    while (this._speedArray.length > 55) this._speedArray.shift();
    for (let i = 0; i < this._speedArray.length; i++) this._speedAverage += this._speedArray[i];
    this._speedAverage /= -48 * this._speedArray.length;
    this._music.volume = Math.min(6, Math.max(0, this._speedAverage - 2)) / 6;
    this._cameraTarget.x = this._cameraTarget.x * 0.8 + (this._horse._body1.position.x - 80) * 0.2;
    this._cameraTarget.y = this._cameraTarget.y * 0.95 + (this._horse._body1.position.y - 10) * 0.05;
    this.handleCollisions();
    if (keys.justPressed('R') && !this._resetting && !this._gameOver) this.reset();
    if (keys.pressed('P') && keys.pressed('Z')) this._save.erase();
    if (!this._pause) {
      for (let i = 0; i < 8; i++) this._space.step(1 / 480, 17, 7);
    }
    if (this._numTouchingGround === 0) {
      ++this._noConFrames;
      if (this._noConFrames > 20) this._horse.neigh();
    } else {
      this._noConFrames = 0;
    }
    this._thudTimer += FlxG.elapsed;
    super.update();
  }

  collisionBegin(cb) {
    if (!cb.arbiters.iterator().hasNext()) return;
    const b1 = cb.int1.castBody, b2 = cb.int2.castBody;
    const arb = cb.arbiters.iterator().next();
    if (b1.userData.name === 'ground') {
      ++this._numTouchingGround;
      if (b2.userData.name === 'hair') {
        if (!this._resetting) this.deadHorse(this.randomString());
      }
    }
    const contacts = arb.collisionArbiter.contacts.iterator();
    if (this._horseAlive) {
      while (contacts.hasNext()) {
        const c = contacts.next();
        if (b2.userData.name === 'foot' && c.fresh) {
          const progress = (c.position.x - 242) / 77 * -1;
          if (progress > this._score) {
            if (!this._loserMode) {
              this._score = progress;
              TweenNano.to(this._modelHorse, 0.3, {
                x: FlxG.width - 20 - progress * 0.01 * (320 - 20),
                y: FlxG.height - 22 + 2 * Math.sin(this._modelHorse.x * 10),
              });
            }
          }
        } else if (c.fresh) {
          // The SWF's Nape beta reported impulses as the sum of its two solver
          // passes (jAcc + pjAcc), about twice what 2.0.22 reports.
          const impulse = arb.totalImpulse(b1, true).length * 2;
          if (this._thudTimer > 0.5) {
            this._thud.volume = Math.max(0, Math.min(1, impulse / 40));
            this._thud.play();
            this._thudTimer = 0;
          }
        }
      }
    }
  }

  collisionOngoing(cb) {
    if (!cb.arbiters.iterator().hasNext()) return;
    const b1 = cb.int1.castBody, b2 = cb.int2.castBody;
    if (b2.userData.name === 'foot') {
      const arb = cb.arbiters.iterator().next();
      if (this._horseAlive) {
        const t = arb.collisionArbiter.tangentImpulse(b1, false);
        t.x *= 2; t.y *= 2; t.z *= 2; // see the totalImpulse note in collisionBegin
        if (t.length >= 25 && t.x > 7) {
          this._puffs.setXSpeed(t.x * 5 - 5, t.x * 5 + 25);
          this._puffs.setYSpeed(t.y * 5 - 85, t.y * 5 - 25);
          const contacts = arb.collisionArbiter.contacts.iterator();
          while (contacts.hasNext()) {
            const c = contacts.next();
            this._puffs.x = c.position.x;
            this._puffs.y = c.position.y;
            this._puffs.emitParticle();
          }
        }
      }
    }
  }

  collisionEnd(cb) {
    const b1 = cb.int1.castBody;
    if (b1.userData.name === 'ground') --this._numTouchingGround;
  }

  deadHorse(text) {
    if (this._gameOver) return;
    this._deathSnd.play();
    this._horse._controlsEnabled = false;
    this._horseAlive = false;
    const h = this._horse;
    for (const j of [h.j_lb1m, h.j_lb2m, h.j_lb3m, h.j_lb4m, h.j_rb1m, h.j_rb2m, h.j_rb3m, h.j_rb4m,
      h.j_lf1m, h.j_lf2m, h.j_lf3m, h.j_lf4m, h.j_rf1m, h.j_rf2m, h.j_rf3m, h.j_rf4m, h.j_b1m, h.j_b2m]) j.active = false;
    this._timmyBubble.speech.text = text;
    this._timmyBubble.show(0);
    if (this._score > this._highScore) {
      this._highScore = this._score;
      this._save.data.highScore = this._score;
      this._save.flush();
    }
    TweenNano.delayedCall(2, () => this.reset());
    this._gameOver = true;
  }

  handleCollisions() {
    const x = this._horse._body2.position.x, y = this._horse._body2.position.y;
    if (x < -7600 && !this._pause) {
      this._horse._controlsEnabled = false;
      this._pause = true;
      TweenNano.delayedCall(0.5, () => this.showEndGame());
    }
    if (x < 5770 && x > -6060) {
      if (y > 530 && this._horse._controlsEnabled) {
        if (Math.random() < 0.5) this.deadHorse('Haw! Ye fell down breakleg ravine!!');
        else this.deadHorse("'Twas my plan all along, haw haw haw!");
      }
    }
  }

  showEndGame() {
    if (this._gameOver) return;
    this._endSnd.play();
    this._pause = true;
    const winner = new FlxSprite(FlxG.width / 2 - 100, FlxG.height, 'PlayState_ImgWinner');
    winner.scrollFactor = new FlxPoint(0, 0);
    winner.alpha = 0;
    this.FrontLayer.add(winner);
    TweenNano.to(winner, 0.8, { y: FlxG.height / 2 - 94, alpha: 1 });
    const t = new FlxText(FlxG.width / 2 - 150, FlxG.height + 168, 300, 'No... CLOP! Augh!', true);
    if (this._save.data.hasWon) t.text = 'Time: ' + this._timeElapsed.toFixed(1) + 's';
    t.scrollFactor = new FlxPoint(0, 0);
    t.alpha = 0;
    this.FrontLayer.add(t);
    t.setFormat('EnglishTowne', 19, 0xFFFFFF80, 'center', 0xFF000000);
    TweenNano.to(t, 1, { y: FlxG.height / 2 + 74, alpha: 1 });
    this._save.data.hasWon = true;
    if (this._save.data.bestTime != null) {
      if (this._timeElapsed < this._save.data.bestTime) this._save.data.bestTime = this._timeElapsed;
    } else {
      this._save.data.bestTime = this._timeElapsed;
    }
    this._save.flush();
    this._gameOver = true;
    this._endKeyListener = true;
  }

  randomString() {
    // int(Math.random() * 100) indexes a 76-entry switch; 76..99 hit the default.
    const i = (Math.random() * 100) | 0;
    let s = i < CLOP_DATA.insults.length ? CLOP_DATA.insults[i] : "Haw, ye silly ol' horse!";
    const n = this._deathNum;
    if (n === 10) s = "That's ten times now, ye lackwitted meatpie!";
    if (n === 20) s = "Haw, you'll never climb that hill.";
    if (n === 40) s = "If the game has slow'd down much, try reloading yon webpage!";
    if (n === 60) s = 'Three-score flips upon thy back, HEE HAW HEE HAW!';
    if (n === 80) s = 'Didst thou take an arrow to the knee?';
    if (n === 100) s = "Well I'll give you this: thou art a persistent beast.";
    if (n === 200) s = 'Turn off the computer and go to sleep';
    return s;
  }

  reset() {
    if (this._resetting) return;
    FlxG.fade(0xff000000, 1, () => this.onFade());
    this._music.fadeOut(0.7);
    this._wind.fadeOut(0.7);
    this._resetting = true;
    this.active = false;
    this.visible = false;
    this._endKeyListener = false;
  }

  onFade() {
    if (!this._resetting) return;
    this._resetting = false;
    this._space.clear();
    this._space = null;
    this._layerArray = null;
    ++this._deathNum;
    FlxG.resetInput();
    FlxG.switchState(new PlayState(true, this._deathNum));
  }

  destroy() {
    this._images = null;
    // Created every life but only added to a group in some lives; destroy
    // them explicitly so they leave FlxG.texts (destroying twice is harmless).
    this._timeText.destroy();
    this._focusText.destroy();
    super.destroy();
  }

  setupWorld() {
    this._space = new Space(new Vec2(0, this._gravity), Broadphase.SWEEP_AND_PRUNE);
  }

  makeSprite(type, offset = 0) {
    const img = FlxG.images[this._images[type]];
    const s = new LowFlxSprite();
    s.ID = type;
    const ratio = img.width / img.height;
    if (ratio > 1 && ratio % 1 < 0.01) {
      const n = ratio | 0;
      s.loadGraphic(img, true, true, img.height, img.height, false);
      const frames = [];
      for (let k = 0; k < ratio; k++) frames.push((k + offset) % n);
      s.addAnimation('default', frames, 7, true);
      s.play('default');
    } else {
      s.loadGraphic(img, false, true, img.width, img.height, false);
      s.active = false;
    }
    s.scrollFactor = this._layerScrollFactors[this._currentLayer];
    return s;
  }

  processLevelData() {
    const raw = CLOP_DATA.levelData.split('%');
    const layers = [];
    this._itemCount = 0;
    for (let i = 1; i < raw.length; i++) {
      layers.push(raw[i].split(';').map((item) => item.split(',')));
    }
    for (let i = 0; i < layers.length; i++) {
      let count = 0;
      for (let j = 0; j < layers[i].length; j++) {
        const item = layers[i][j];
        const type = Number(item[0]) | 0;
        const s = this.makeSprite(type, count);
        s.facing = Number(item[3]) | 0;
        s.x = Number(item[1]);
        s.y = Number(item[2]);
        s.scrollFactor = this._layerScrollFactors[i];
        this._layerArray[i].add(s);
        if (!s.onScreen()) s.active = false;
        ++this._itemCount;
        ++count;
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Boot: preloader (Preloader.as) + asset loading + game start
// ---------------------------------------------------------------------------
const CLOP = {
  IMAGES: [
    'Horse_Body1Img', 'Horse_Body2Img', 'Horse_Body3Img', 'Horse_Body4Img', 'Horse_HairImg', 'Horse_HeadImg', 'Horse_HornImg',
    'Horse_LBLeg1Img', 'Horse_LBLeg2Img', 'Horse_LBLeg3Img', 'Horse_LBLeg4Img', 'Horse_LFLeg1Img', 'Horse_LFLeg2Img', 'Horse_LFLeg3Img', 'Horse_LFLeg4Img',
    'Horse_RBLeg1Img', 'Horse_RBLeg2Img', 'Horse_RBLeg3Img', 'Horse_RBLeg4Img', 'Horse_RFLeg1Img', 'Horse_RFLeg2Img', 'Horse_RFLeg3Img', 'Horse_RFLeg4Img',
    'Horse_Tail1Img', 'Horse_Tail2Img', 'Horse_Tail3Img', 'Horse_Tail4Img',
    'KeyGraphic_ImgKeys', 'Star_StarImg', 'SpeechBubble_ImgClop', 'SpeechBubble_ImgRibbon', 'SpeechBubble_ImgTimmy',
    'PlayState_ImgSky', 'PlayState_ImgCopyright', 'PlayState_ImgHS', 'PlayState_ImgModelGirl', 'PlayState_ImgModelHorse', 'PlayState_ImgModelTree', 'PlayState_ImgWinner',
    'PlayState_ImgGround00', 'PlayState_ImgGround01', 'PlayState_ImgGround02', 'PlayState_ImgGround03', 'PlayState_ImgGround04', 'PlayState_ImgGround05',
    'PlayState_ImgGround06', 'PlayState_ImgGround07', 'PlayState_ImgGround08', 'PlayState_ImgGround09', 'PlayState_ImgGround10',
  ].concat(LEVEL_IMAGES),
  SOUNDS: ['Clop1Snd', 'Clop2Snd', 'Clop3Snd', 'Clop4Snd', 'EndSnd', 'MusicSnd', 'Neigh1Snd', 'Neigh2Snd', 'SnortSnd', 'StartSnd', 'ThudSnd', 'WhinnySnd', 'WindSnd'],
  // DefineSound headers from CLOP.swf: sample rate, sample count, SeekSamples.
  SOUND_INFO: {
    Clop1Snd: { rate: 44100, samples: 5757, seek: 1695 },
    Clop2Snd: { rate: 44100, samples: 3414, seek: 1695 },
    Clop3Snd: { rate: 44100, samples: 4410, seek: 1695 },
    Clop4Snd: { rate: 44100, samples: 3969, seek: 1695 },
    ThudSnd: { rate: 44100, samples: 3045, seek: 1683 },
    WindSnd: { rate: 22050, samples: 45377, seek: 1681 },
    EndSnd: { rate: 22050, samples: 41955, seek: 1673 },
    Neigh1Snd: { rate: 22050, samples: 35360, seek: 1672 },
    Neigh2Snd: { rate: 22050, samples: 14181, seek: 1672 },
    MusicSnd: { rate: 22050, samples: 197832, seek: 1672 },
    WhinnySnd: { rate: 22050, samples: 9360, seek: 1672 },
    StartSnd: { rate: 22050, samples: 21936, seek: 1672 },
    SnortSnd: { rate: 22050, samples: 16750, seek: 1672 },
  },

  // Loading screen geometry fitted to Preloader_ImgGreyBG.png (640x480 units).
  LOADING: { bg: '#898989', gold: '#ffd200', lineY: 117.2, lineH: 5.8, size: 38, right: 632.75, baseline: 152.5, shadowA: 0.9, blur: 1.5, ox: 0.5, oy: 1 },
  // The sky JPEG is a purely vertical multi-stop gradient (measured: hue sweeps
  // 220deg -> 342deg at ~50% saturation; 3 interior stops fit it to within JPEG
  // noise). In HD mode regenerate it at full resolution instead of upscaling.
  SKY_STOPS: [[0, '#4e6aa2'], [0.238, '#815fb5'], [0.515, '#b560bd'], [0.803, '#e46fb5'], [1, '#ff83a8']],
  makeSkyGradient(scale) {
    const c = document.createElement('canvas');
    c.width = 320 * scale; c.height = 240 * scale;
    const ctx = c.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 0, c.height);
    for (const [pos, col] of this.SKY_STOPS) g.addColorStop(pos, col);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, c.width, c.height);
    c.prescaled = true;
    return c;
  },
  // Both modes present at an exact integer multiple of 320x240, 1:1 on screen,
  // letterboxed. Classic: buffer at 2x drawn at the largest multiple that fits.
  // HD: render scale = that multiple, presented 1:1.
  fitCanvas(canvas, hd) {
    const game = window.CLOP_GAME;
    if (game) {
      if (hd === 1) game.presentScale = this.fitScale();
      game.onScaleChange();
    }
    canvas.style.width = canvas.width + 'px'; canvas.style.height = canvas.height + 'px';
    canvas.style.imageRendering = 'pixelated';
  },
  // Largest integer multiple of 320x240 that fits the window (never stretch).
  fitScale() { return Math.max(1, Math.min(8, Math.floor(Math.min(window.innerWidth / 320, window.innerHeight / 240)))); },
  autoHdScale() { return Math.max(2, this.fitScale()); },
  // Switch between classic (scale 1) and HD (scale N) at runtime.
  applyMode(hd) {
    this.hd = hd;
    FlxG.images.PlayState_ImgSky = hd > 1 ? this.makeSkyGradient(hd) : this.skyJpg;
    FlxG.setScale(hd);
    this.fitCanvas(this.canvas, hd);
    try { localStorage.setItem('CLOP:hd', hd > 1 ? '1' : '0'); } catch (e) {}
    if (this.toggle) this.toggle.textContent = hd > 1 ? 'HD ' + hd + 'x  (click for classic)' : 'Classic  (click for HD)';
  },
  makeToggle() {
    const b = document.createElement('button');
    b.id = 'hdtoggle';
    b.tabIndex = -1; // never focusable: H/J/K/L must keep reaching the game
    b.style.cssText = 'position:fixed;top:8px;right:8px;z-index:10;font:12px monospace;padding:4px 8px;background:rgba(0,0,0,.55);color:#ddd;border:1px solid #666;border-radius:3px;cursor:pointer;opacity:.35';
    b.onmouseenter = () => { b.style.opacity = '1'; }; b.onmouseleave = () => { b.style.opacity = '.35'; };
    // Don't let the click reach the window: it would also count as "clicketh mouse to begin".
    b.addEventListener('pointerdown', (e) => e.stopPropagation());
    b.addEventListener('keydown', (e) => e.stopPropagation());
    b.addEventListener('click', (e) => { e.stopPropagation(); this.applyMode(this.hd > 1 ? 1 : this.autoHdScale()); b.blur(); });
    document.body.appendChild(b);
    return b;
  },
  async boot(canvas) {
    this.canvas = canvas;
    // The SWF's Nape beta used a 0.25 px collision slop (2.0.22 defaults to 0.2).
    NAPE.Config.collisionSlop = 0.25;
    // The beta's sleep test compares speed^2 > 0.2 and 4*w^2*d^2 > 0.4; 2.0.22
    // squares these Config values, so sqrt() gives identical behaviour.
    NAPE.Config.linearSleepThreshold = Math.sqrt(0.2);
    NAPE.Config.angularSleepThreshold = Math.sqrt(0.4);
    const params = new URLSearchParams(location.search);
    // ?hd=N renders the scene at N x 320x240 (full-res text, sky and rotation,
    // everything else nearest-neighbour at an exact integer scale). Without the
    // parameter the last choice made with the on-screen toggle is used.
    let hd = Math.max(1, Math.min(8, Math.floor(Number(params.get('hd')) || 1)));
    let stored = null; try { stored = localStorage.getItem('CLOP:hd'); } catch (e) {}
    if (params.get('hd') == null && stored === '1') hd = this.autoHdScale();
    this.hd = hd;
    // Keyboard events are always delivered on Flash's 30 fps frame clock, so
    // the tick/key-state sequence matches the SWF. Classic mode also paces the
    // whole loop at 30 fps like Flash Player; HD mode draws every animation
    // frame. ?fps=30 / ?fps=60 overrides.
    const fpsParam = params.get('fps');
    const frameRate = fpsParam == null ? (hd > 1 ? 0 : 30) : (Number(fpsParam) >= 60 ? 0 : (Number(fpsParam) || 0));
    const autoFrameRate = fpsParam == null;
    const inputRate = 30;
    FlxG.scale = hd;
    const sctx = canvas.getContext('2d', { alpha: false });
    canvas.width = 640; canvas.height = 480;
    sctx.imageSmoothingEnabled = false;
    this.fitCanvas(canvas, hd);
    try { FlxG.audio = new (window.AudioContext || window.webkitAudioContext)(); } catch (e) { FlxG.audio = null; }
    const unlock = () => { if (FlxG.audio && FlxG.audio.state === 'suspended') FlxG.audio.resume(); };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    // Preloader: grey background, horse walks across as assets load (min 500 ms).
    const preImgs = await Promise.all([
      FlxAssets.loadImage('Preloader_ImgGreyBG', 'assets/images/Preloader_ImgGreyBG.png'),
      FlxAssets.loadImage('Preloader_ImgPrehorse', 'assets/images/Preloader_ImgPrehorse.png'),
    ]);
    // The loading screen is shown at the size the game will be presented at
    // (an integer multiple of 320x240 in both modes). At exactly 640x480 it
    // is the SWF's bitmap; at any other size it is re-rendered procedurally:
    // the grey background, gold rule and "Loading..." in EnglishTowne with a
    // drop shadow, parameters fitted against the bitmap (test/loading-fit.html).
    const k = (hd > 1 ? 320 * hd : 320 * this.fitScale()) / 640;
    if (k !== 1) { canvas.width = 640 * k; canvas.height = 480 * k; await document.fonts.load('38px "EnglishTowne"'); }
    this.fitCanvas(canvas, hd);
    const drawLoadingBackground = () => {
      if (k === 1) { sctx.drawImage(preImgs[0], 0, 0, 640, 480); return; }
      const L = this.LOADING;
      sctx.save();
      sctx.fillStyle = L.bg; sctx.fillRect(0, 0, canvas.width, canvas.height);
      sctx.fillStyle = L.gold; sctx.fillRect(0, L.lineY * k, canvas.width, L.lineH * k);
      sctx.font = (L.size * k) + 'px "EnglishTowne"'; sctx.textAlign = 'right'; sctx.textBaseline = 'alphabetic';
      sctx.shadowColor = 'rgba(0,0,0,' + L.shadowA + ')'; sctx.shadowBlur = L.blur * k; sctx.shadowOffsetX = L.ox * k; sctx.shadowOffsetY = L.oy * k;
      sctx.fillText('Loading...', L.right * k, L.baseline * k);
      sctx.restore();
    };
    const t0 = performance.now();
    const total = this.IMAGES.length + this.SOUNDS.length + 1;
    let loaded = 0;
    const tasks = [];
    for (const n of this.IMAGES) tasks.push(FlxAssets.loadImage(n, 'assets/images/' + n + (n === 'PlayState_ImgSky' ? '.jpg' : '.png')).then(() => loaded++));
    for (const n of this.SOUNDS) tasks.push(FlxAssets.loadSound(n, 'assets/sounds/' + n + '.mp3', this.SOUND_INFO[n]).then(() => loaded++, (e) => { console.warn(e); loaded++; }));
    tasks.push(document.fonts.load('19px "EnglishTowne"').then(() => loaded++, () => loaded++));
    let done = false;
    Promise.all(tasks).then(() => { done = true; });
    await new Promise((resolve) => {
      const frame = () => {
        const elapsed = performance.now() - t0;
        let p = loaded / total;
        if (p > elapsed / 500) p = elapsed / 500;
        sctx.imageSmoothingEnabled = false;
        drawLoadingBackground();
        sctx.imageSmoothingEnabled = false; // the preloader horse is a flat-colour silhouette
        sctx.drawImage(preImgs[1], Math.trunc(p * 696 - 56) * k, 64 * k, 56 * k, 54 * k);
        if (done && elapsed > 500) resolve(); else requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    });

    this.skyJpg = FlxG.images.PlayState_ImgSky;
    if (hd > 1) FlxG.images.PlayState_ImgSky = this.makeSkyGradient(hd);
    FlxG.rotateAtDraw = hd > 1;
    const game = new FlxGame(320, 240, PlayState, 2, 60, 30, { canvas: canvas, frameRate: frameRate, inputRate: inputRate, autoFrameRate: autoFrameRate, presentScale: this.fitScale() });
    window.CLOP_GAME = game;
    this.fitCanvas(canvas, hd);
    this.toggle = this.makeToggle();
    this.toggle.textContent = hd > 1 ? 'HD ' + hd + 'x  (click for classic)' : 'Classic  (click for HD)';
    // Both modes follow the viewport: classic re-picks its integer multiple,
    // HD re-picks its render scale. Installed only now: applyMode() before
    // the game exists would swap the sky before skyJpg is captured.
    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      this.fitCanvas(canvas, this.hd);
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { if (this.hd > 1 && this.autoHdScale() !== this.hd) this.applyMode(this.autoHdScale()); }, 200);
    });
    // Mouse clicks reach the game on the same frame clock as keys (Flash's ACTIVATE event).
    const activate = () => { unlock(); game.queueInput('activate', null); };
    window.addEventListener('pointerdown', activate);
    window.addEventListener('keydown', activate);
    game.start();
    window.CLOP_GAME = game;
  },
};
